/**
 * GuideCardContent — B-2026-0088 Phase A1
 *
 * Renders a `StructuredGuideBody` as:
 *   - Lead sentence (font-medium, primary colour)
 *   - Cyan-dot bullet list (2-4 points)
 *   - "Look for:" call-to-action (muted italic, only if present)
 *   - "Learn more →" link (only if learnMoreUrl is set)
 *
 * Accepts either a `StructuredGuideBody` directly, or a plain string (legacy)
 * which it wraps as `{ lead: str, points: [] }` via `resolveGuideBody()`.
 *
 * Used by both EvalResultsGuideOverlay and StudioGuideOverlay so both surfaces
 * get the same structured layout without code duplication.
 */

import type { StructuredGuideBody } from '@/data/guideContentTypes';
import { isStructuredBody } from '@/data/guideContentTypes';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface GuideCardContentProps {
  /**
   * The resolved guide body to render.
   * Pass the result of `resolveGuideBody(entry, eval_)`.
   */
  body: StructuredGuideBody | string;
  /** Whether the anchor element is offscreen — shows a scroll hint. */
  isOffscreen?: boolean;
  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GuideCardContent({ body, isOffscreen = false, className = '' }: GuideCardContentProps) {
  // Normalise a plain legacy string into the structured shape.
  const structured: StructuredGuideBody = isStructuredBody(body)
    ? body
    : { lead: String(body), points: [] };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {/* Lead sentence */}
      <p
        className="text-sm font-medium leading-snug"
        style={{ color: 'var(--color-text-primary)' }}
      >
        {structured.lead}
      </p>

      {/* Bullet points */}
      {structured.points.length > 0 && (
        <ul className="flex flex-col gap-1" role="list">
          {structured.points.map((point, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-sm leading-snug"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {/* Cyan dot */}
              <span
                aria-hidden="true"
                className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full"
                style={{ backgroundColor: 'var(--color-accent-cyan)' }}
              />
              <span>{point}</span>
            </li>
          ))}
        </ul>
      )}

      {/* "Look for:" hint */}
      {structured.lookFor && (
        <p
          className="text-xs italic leading-snug"
          style={{ color: 'var(--color-text-muted)' }}
        >
          <span style={{ color: 'var(--color-accent-cyan)', fontStyle: 'normal', fontWeight: 500 }}>
            Look for:
          </span>{' '}
          {structured.lookFor}
        </p>
      )}

      {/* Offscreen scroll hint */}
      {isOffscreen && (
        <p
          className="text-xs leading-snug"
          style={{ color: 'var(--color-text-muted)' }}
          aria-live="polite"
        >
          ↕ Scroll to see the highlighted element
        </p>
      )}

      {/* Learn more link */}
      {structured.learnMoreUrl && (
        <a
          href={structured.learnMoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 inline-block text-xs font-medium"
          style={{ color: 'var(--color-accent-cyan)' }}
        >
          Learn more &rarr;
        </a>
      )}
    </div>
  );
}

export default GuideCardContent;
